/** ALL MODULES — searchable directory of every destination in SUNTRONIX ONE. */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Search, ArrowRight } from "lucide-react";
import { OS_NAV, OS_NAV_BOTTOM, OS_DIRECTORY } from "@/constants/osNav";

export default function ModuleDirectory({ open, onOpenChange, visible }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const groups = useMemo(() => {
    const all = [...OS_NAV, ...OS_NAV_BOTTOM];
    return all.map((g) => {
      const items = OS_DIRECTORY.filter((d) => d.groupId === g.id && visible(d) &&
        (!ql || d.label.toLowerCase().includes(ql) || (d.space || "").toLowerCase().includes(ql) || g.label.toLowerCase().includes(ql) || d.to.toLowerCase().includes(ql)));
      return { ...g, items };
    }).filter((g) => g.items.length > 0);
  }, [ql, visible]);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const go = (to) => { onOpenChange(false); setQ(""); navigate(to); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:max-w-3xl p-0 flex flex-col" data-testid="module-directory">
        <SheetHeader className="px-5 pt-5 pb-3 text-left border-b border-[color:var(--st-border)]">
          <SheetTitle className="font-display text-xl">All modules <span className="text-[color:var(--st-text-subtle)] text-sm font-normal ml-1">{total}</span></SheetTitle>
          <SheetDescription className="text-xs">Every feature in SUNTRONIX ONE, grouped by department. Search by name, department or page address — only pages you are authorised to open are listed.</SheetDescription>
          <div className="relative mt-2">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--st-text-subtle)]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="module-directory-search"
              placeholder="Search modules… e.g. quote, dispatch, whatsapp, warranty, ledger"
              className="w-full h-10 pl-9 pr-3 rounded-full border border-[color:var(--st-border)] bg-[color:var(--st-bg)] text-sm outline-none focus:border-[color:var(--st-accent)] focus:ring-4 focus:ring-[color:var(--st-accent)]/15" />
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {groups.length === 0 && <div className="text-sm text-[color:var(--st-text-muted)] py-10 text-center" data-testid="module-directory-empty">No module matches “{q}”.</div>}
          {groups.map((g) => {
            const Icon = g.icon;
            return (
              <section key={g.id} data-testid={`module-dir-${g.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[color:var(--st-accent)]" strokeWidth={1.8} />
                  <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[color:var(--st-text-muted)]">{g.label}</h3>
                  <span className="text-[10px] text-[color:var(--st-text-subtle)]">{g.items.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {g.items.map((d) => (
                    <button key={d.to} onClick={() => go(d.to)} data-testid={`module-dir-item-${d.to.split("?")[0].replace(/\//g, "-").replace(/^-/, "")}`}
                      className="group text-left rounded-lg border border-[color:var(--st-border)] bg-white px-3 py-2 hover:border-[color:var(--st-accent)]/50 hover:bg-[color:var(--st-accent-soft)] transition-colors flex items-center gap-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-medium text-[color:var(--st-text)] truncate">{d.label}</span>
                        {d.space && <span className="block text-[10.5px] text-[color:var(--st-text-subtle)] truncate">{d.space}</span>}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-[color:var(--st-text-hint)] group-hover:text-[color:var(--st-accent)] transition-colors" />
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
